import { getDifferences } from '../../../utils/objectFunctions';
import parsePhoneNumberFromString from 'libphonenumber-js';

export const SUBSIDISED_PRODUCT_TYPE_ID = '26a41eb6-a9d1-4d2f-88e5-1bb59bdb9ae1';

const isEmptyContactValue = (value: any): boolean => {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string') return value.trim() === '';
  return false;
};

const normalizeContactEmail = (email: string): string => email.trim().toLowerCase();

const normalizeContactEmails = (emails: any[]): string[] => {
  const normalized = emails
    .filter((email) => !isEmptyContactValue(email))
    .map((email) => normalizeContactEmail(String(email)));

  return Array.from(new Set(normalized));
};

const normalizeContactPhone = (phone: string): string => {
  const parsed = parsePhoneNumberFromString(String(phone), 'ES');

  if (parsed) {
    return parsed.number;
  }

  return String(phone).replace(/\D+/g, '');
};

const normalizeContactPhones = (phones: any[]): string[] => {
  const normalized = phones
    .filter((phone) => !isEmptyContactValue(phone))
    .map((phone) => normalizeContactPhone(String(phone)));

  return Array.from(new Set(normalized));
};

const hasContactCollision = (firstSet: string[], secondSet: string[]): boolean => {
  if (!firstSet.length || !secondSet.length) {
    return false;
  }

  return firstSet.some((value) => secondSet.includes(value));
};

export type CursoBonificadoContactValidationError = {
  message: string;
  path: string;
  value: any;
};

export const getCursoBonificadoUniqueContactValidationError = (
  values: Record<string, any>
): CursoBonificadoContactValidationError | null => {
  if (values.productTypeId !== SUBSIDISED_PRODUCT_TYPE_ID) {
    return null;
  }

  const clientEmails = normalizeContactEmails([values.clientEmail]);
  const studentEmails = normalizeContactEmails([values.studentEmail]);

  if (hasContactCollision(clientEmails, studentEmails)) {
    return {
      message: 'El email de la empresa y el del alumno no pueden coincidir en contratos de Curso Bonificado',
      path: 'clientEmail',
      value: values.clientEmail,
    };
  }

  const clientPhones = normalizeContactPhones([
    values.clientTelephone,
    values.clientTelephoneSecondary,
    values.clientTelephoneThird,
    values.clientMobile,
  ]);
  const studentPhones = normalizeContactPhones([
    values.studentTelephone,
    values.studentTelephoneSecondary,
    values.studentMobile,
  ]);

  if (hasContactCollision(clientPhones, studentPhones)) {
    return {
      message: 'El teléfono de la empresa y el del alumno no pueden coincidir en contratos de Curso Bonificado',
      path: 'clientTelephone',
      value: values.clientTelephone,
    };
  }

  return null;
};

const isEmptyValue = (value: any): boolean => {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string') return value.trim() === '';
  return false;
};

const getFirstNonEmptyValue = (...values: any[]) => {
  const value = values.find((entry) => !isEmptyValue(entry));
  return value === undefined ? '' : value;
};

const hasAnyValue = (...values: any[]) => values.some((value) => !isEmptyValue(value));

const parseBooleanValue = (value: any): boolean => [true, 1, '1', 'true', 't'].includes(value);

// Configuración de mapeo de campos por entidad
export const ENTITY_FIELD_CONFIGS = {
  client: {
    fields: [
      'clientId', 'clientActivity', 'clientAddress', 'clientCifNif', 'clientCity',
      'clientCnae', 'clientCompanyCreationDate', 'clientCompanyName', 'clientEmail',
      'clientIban', 'clientPostalCode', 'clientProvince', 'clientSocialSecurityNumber',
      'clientTelephone', 'clientTelephoneSecondary', 'clientTelephoneThird',
      'clientWorkersNumber', 'clientHasRlt', 'clientMobile', 'clientBic'
    ],
    permission: 'edit_client_on_contract',
    successMessage: 'Datos del cliente actualizados correctamente',
    errorMessage: 'Error al actualizar los datos del cliente',
    serviceMethod: 'editClientOnContract'
  },
  student: {
    fields: [
      'studentId', 'studentStudyId', 'studentQuotationGroupId', 'studentProfessionalCategoryId',
      'studentName', 'studentFirstName', 'studentLastName', 'studentNif', 'studentGender',
      'studentTerrorismVictim', 'studentGenderViolenceVictim', 'studentDisability',
      'studentSocialSecurityNumber', 'studentFunctionalArea', 'studentEmail',
      'studentTelephone', 'studentTelephoneSecondary', 'studentAlternativeShippingAddress',
      'studentContactSchedule', 'studentBirthDate', 'studentMobile', 'studentComment'
    ],
    permission: 'edit_student_on_contract',
    successMessage: 'Datos del empleado actualizados correctamente',
    errorMessage: 'Error al actualizar los datos del empleado',
    serviceMethod: 'editStudentOnContract'
  },
  consultancy: {
    fields: [
      'consultancyId', 'consultancyName', 'consultancyEmail', 'consultancyCif',
      'consultancyContact', 'consultancyTelephone', 'consultancyTelephoneSecondary',
      'consultancyDescription'
    ],
    permission: 'edit_consultancy_on_contract',
    successMessage: 'Datos de la asesoría actualizados correctamente',
    errorMessage: 'Error al actualizar los datos de la asesoría',
    serviceMethod: 'editConsultancyOnContract'
  }
};

const ENTITY_SERVICE_FIELD_MAPPINGS: Partial<Record<keyof typeof ENTITY_FIELD_CONFIGS, Record<string, string>>> = {
  client: {
    clientAddress: 'clientRegisteredOfficeAddress',
    clientCity: 'clientRegisteredOfficeTown',
    clientProvince: 'clientRegisteredOfficeProvince',
    clientPostalCode: 'clientRegisteredOfficePostalCode',
    clientActivity: 'clientActivityDescription',
  },
};

const normalizeComparableValue = (value: any) => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value.trim();
  return value;
};

const normalizeComparableEntityData = (data: Record<string, any>) => {
  return Object.keys(data).reduce((acc: Record<string, any>, key) => {
    acc[key] = normalizeComparableValue(data[key]);
    return acc;
  }, {});
};

const mapEntityDifferencesToServicePayload = (
  differences: Record<string, any>,
  entityType: keyof typeof ENTITY_FIELD_CONFIGS
) => {
  const fieldMappings = ENTITY_SERVICE_FIELD_MAPPINGS[entityType] ?? {};

  return Object.keys(differences).reduce((acc: Record<string, any>, key) => {
    const serviceKey = fieldMappings[key] ?? key;
    acc[serviceKey] = differences[key];
    return acc;
  }, {});
};

// Configuración de campos para valores iniciales
export const INITIAL_VALUES_CONFIG = {
  // Campos del cliente
  clientId: (data: any) => data?.client?.id ?? data?.clientId ?? '',
  clientActivity: (data: any) => getFirstNonEmptyValue(data?.clientActivity, data?.client?.activityDescription),
  clientAddress: (data: any) => getFirstNonEmptyValue(data?.clientAddress, data?.client?.registeredOfficeAddress),
  clientCifNif: (data: any) => getFirstNonEmptyValue(data?.clientCifNif, data?.client?.cifNif),
  clientCity: (data: any) => getFirstNonEmptyValue(data?.clientCity, data?.client?.registeredOfficeTown),
  clientCnae: (data: any) => getFirstNonEmptyValue(data?.clientCnae, data?.client?.cnae),
  clientCompanyCreationDate: (data: any) => extractDateValue(getFirstNonEmptyValue(data?.clientCompanyCreationDate, data?.client?.companyCreationDate)),
  clientCompanyName: (data: any) => getFirstNonEmptyValue(data?.clientCompanyName, data?.client?.companyName),
  clientEmail: (data: any) => getFirstNonEmptyValue(data?.clientEmail, data?.client?.email),
  clientIban: (data: any) => getFirstNonEmptyValue(data?.clientIban, data?.client?.iban),
  clientPostalCode: (data: any) => getFirstNonEmptyValue(data?.clientPostalCode, data?.client?.registeredOfficePostalCode),
  clientProvince: (data: any) => getFirstNonEmptyValue(data?.clientProvince, data?.client?.registeredOfficeProvince),
  clientSocialSecurityNumber: (data: any) => getFirstNonEmptyValue(data?.clientSocialSecurityNumber, data?.client?.socialSecurityNumber),
  clientTelephone: (data: any) => getFirstNonEmptyValue(data?.clientTelephone, data?.client?.telephone),
  clientTelephoneSecondary: (data: any) => getFirstNonEmptyValue(data?.clientTelephoneSecondary, data?.client?.telephoneSecondary),
  clientTelephoneThird: (data: any) => getFirstNonEmptyValue(data?.clientTelephoneThird, data?.client?.telephoneThird),
  clientWorkersNumber: (data: any) => data?.clientWorkersNumber ?? data?.client?.workersNumber ?? 1,
  clientHasRlt: (data: any) => data?.clientHasRlt ?? data?.client?.hasRlt ?? false,
  clientMobile: (data: any) => getFirstNonEmptyValue(data?.clientMobile, data?.client?.mobile),
  clientBic: (data: any) => getFirstNonEmptyValue(data?.clientBic, data?.client?.bic),

  // Campos del producto
  productTypeId: (data: any) => data?.productType?.id ?? '',
  productId: (data: any) => data?.product?.id ?? '',
  complements: (data: any) => data?.complements?.map((c: any) => ({ complementId: c.complements?.id, amount: c.amount })) ?? [],
  productAmount: (data: any) => data?.productAmount ?? 0,
  totalAmount: (data: any) => data?.totalAmount ?? 0,
  studentFieldsRequired: (data: any) => Boolean(
    data?.product?.studentFieldsRequired ??
    hasAnyValue(data?.student?.id, data?.studentId, data?.studentNif, data?.studentName, data?.studentFirstName, data?.studentLastName)
  ),
  consultancyFieldsRequired: (data: any) => Boolean(
    parseBooleanValue(data?.product?.consultancyFieldsRequired) ||
    parseBooleanValue(data?.product?.productTypeConsultancyFieldsRequired) ||
    parseBooleanValue(data?.product?.productType?.consultancyFieldsRequired) ||
    parseBooleanValue(data?.productType?.consultancyFieldsRequired) ||
    hasAnyValue(data?.consultancy?.id, data?.consultancyId, data?.consultancyName, data?.consultancyEmail, data?.consultancyCif)
  ),

  // Campos del contrato
  contractId: (data: any) => data?.id ?? '',
  company: (data: any) => data?.company?.id ?? '',
  campaignId: (data: any) => data?.campaign?.id ?? '',
  commercialId: (data: any, user: any) => data?.commercial?.id ?? user?.id ?? '',
  commercialNetworkId: (data: any, user: any) => {
    const fromData = data?.commercialNetwork?.id;
    if (fromData) return fromData;
    const fromUser = user?.commercialNetwork?.[0];
    return (typeof fromUser === 'object' && fromUser?.id) ? fromUser.id : (fromUser ?? '');
  },
  organisingCorporationId: (data: any) => data?.organisingCorporation?.id ?? '',
  providingCorporationId: (data: any) => data?.providingCorporation?.id ?? '',
  platformServiceCorporationId: (data: any) => data?.platformServiceCorporation?.id ?? '',
  servicesCorporationId: (data: any) => data?.servicesCorporation?.id ?? '',
  groupings: (data: any) => data?.groupings ?? '',
  operatorName: (data: any) => data?.operatorName ?? '',
  tripartiteGrouping: (data: any) => data?.tripartiteGrouping ?? '',
  userLogin: (data: any) => data?.userLogin ?? '',
  tutorComment: (data: any) => data?.tutorComment ?? '',
  saleComment: (data: any) => data?.saleComment ?? '',
  activationDate: (data: any) => extractDateValue(data?.activationDate),
  socialInsuranceAmount: (data: any) => data?.socialInsuranceAmount ?? 0,
  paymentMethodsId: (data: any) => data?.paymentMethods?.id ?? '',
  paymentInstalmentsNumber: (data: any) => data?.paymentInstalmentsNumber ?? 1,
  expirationTypeId: (data: any) => data?.expirationType?.id ?? '',
  draftExpirationDate: (data: any) => extractDateValue(data?.draftExpirationDate),
  productStartDate: (data: any) => extractDateValue(data?.productStartDate),
  productEndDate: (data: any) => extractDateValue(data?.productEndDate),
  contractType: (data: any) => data?.contractType ?? '',
  saveAsContract: () => false,
  saveAsDraft: () => false,

  // Campos del representante legal
  legalRepresentativeExist: () => false,
  legalRepresentativeEmail: (data: any) => data?.legalRepresentativeEmail ?? '',
  legalRepresentativeName: (data: any) => data?.legalRepresentativeName ?? '',
  legalRepresentativeNif: (data: any) => data?.legalRepresentativeNif ?? '',
  legalRepresentativePosition: (data: any) => data?.legalRepresentativePosition ?? '',
  legalRepresentativeSchedule: (data: any) => data?.legalRepresentativeSchedule ?? '',

  // Campos del estudiante
  studentId: (data: any) => data?.student?.id ?? data?.studentId ?? '',
  studentStudyId: (data: any) => data?.studentStudy?.id ?? '',
  studentQuotationGroupId: (data: any) => data?.studentQuotationGroup?.id ?? '',
  studentProfessionalCategoryId: (data: any) => data?.studentProfessionalCategory?.id ?? '',
  studentName: (data: any) => data?.studentName ?? '',
  studentFirstName: (data: any) => data?.studentFirstName ?? '',
  studentLastName: (data: any) => data?.studentLastName ?? '',
  studentNif: (data: any) => data?.studentNif ?? '',
  studentGender: (data: any) => data?.studentGender ?? '',
  studentTerrorismVictim: (data: any) => data?.studentTerrorismVictim ?? false,
  studentGenderViolenceVictim: (data: any) => data?.studentGenderViolenceVictim ?? false,
  studentDisability: (data: any) => data?.studentDisability ?? false,
  studentSocialSecurityNumber: (data: any) => data?.studentSocialSecurityNumber ?? '',
  studentFunctionalArea: (data: any) => data?.studentFunctionalArea ?? '',
  studentEmail: (data: any) => data?.studentEmail ?? '',
  studentTelephone: (data: any) => data?.studentTelephone ?? '',
  studentTelephoneSecondary: (data: any) => data?.studentTelephoneSecondary ?? '',
  studentAlternativeShippingAddress: (data: any) => data?.studentAlternativeShippingAddress ?? '',
  studentContactSchedule: (data: any) => data?.studentContactSchedule ?? '',
  studentBirthDate: (data: any) => extractDateValue(data?.studentBirthDate),
  studentMobile: (data: any) => data?.studentMobile ?? '',
  studentComment: (data: any) => data?.studentComment ?? '',

  // Campos de la consultoría
  consultancyId: (data: any) => data?.consultancy?.id ?? data?.consultancyId ?? '',
  consultancyName: (data: any) => getFirstNonEmptyValue(data?.consultancyName, data?.consultancy?.name),
  consultancyEmail: (data: any) => getFirstNonEmptyValue(data?.consultancyEmail, data?.consultancy?.email),
  consultancyCif: (data: any) => getFirstNonEmptyValue(data?.consultancyCif, data?.consultancy?.cif),
  consultancyContact: (data: any) => getFirstNonEmptyValue(data?.consultancyContact, data?.consultancy?.contact),
  consultancyTelephone: (data: any) => getFirstNonEmptyValue(data?.consultancyTelephone, data?.consultancy?.telephone),
  consultancyTelephoneSecondary: (data: any) => getFirstNonEmptyValue(data?.consultancyTelephoneSecondary, data?.consultancy?.telephoneSecondary),
  consultancyDescription: (data: any) => getFirstNonEmptyValue(data?.consultancyDescription, data?.consultancy?.description)
};

const extractDateValue = (value: any): string => {
  if (!value) return '';
  if (typeof value === 'string') return value.split(' ')[0];
  if (value?.date && typeof value.date === 'string') return value.date.split(' ')[0];
  return '';
};

/**
 * Crea los valores iniciales del formulario usando configuración centralizada
 */
export const createInitialValues = (data: any, user: any) => {
  const initialValues: any = {};

  Object.entries(INITIAL_VALUES_CONFIG).forEach(([key, valueFunction]) => {
    initialValues[key] = valueFunction(data, user);
  });

  return initialValues;
};

export const buildOriginalEntityData = (
  values: Record<string, any>,
  entityType: keyof typeof ENTITY_FIELD_CONFIGS
) => {
  const { fields } = ENTITY_FIELD_CONFIGS[entityType];

  return fields.reduce((acc: Record<string, any>, field) => {
    acc[field] = values[field];
    return acc;
  }, {});
};

/**
 * Función genérica para obtener diferencias entre datos originales y nuevos
 */
export const getEntityDifferences = (
  values: any,
  originalData: any,
  entityType: keyof typeof ENTITY_FIELD_CONFIGS,
  userCan: (action: string, group: any, superAdmin?: boolean) => boolean,
  setDifferencesCallback: (differences: any) => void
) => {
  const config = ENTITY_FIELD_CONFIGS[entityType];

  if (userCan(config.permission, 'contracts') && originalData) {
    const newData = config.fields.reduce((acc: any, key) => {
      acc[key] = values[key];
      return acc;
    }, {});

    const differences = getDifferences(
      normalizeComparableEntityData(originalData),
      normalizeComparableEntityData(newData)
    );
    const servicePayload = mapEntityDifferencesToServicePayload(
      { ...differences, [config.fields[0]]: values[config.fields[0]] },
      entityType
    );

    setDifferencesCallback(servicePayload);
    return differences;
  }

  return null;
};

/**
 * Función genérica para editar datos de entidad en el contrato
 */
export const editEntityData = async (
  values: any,
  entityType: keyof typeof ENTITY_FIELD_CONFIGS,
  contractService: any,
  submitValues: any,
  setLoadingContract: (loading: boolean) => void,
  setLoadingDraft: (loading: boolean) => void,
  submitContract: Function,
  submitDraft: Function,
  handleErrors: (response: any) => void,
  toast: any,
  openConfirmStates: { [key: string]: boolean }
) => {
  const config = ENTITY_FIELD_CONFIGS[entityType];

  submitValues.saveAsContract && setLoadingContract(true);
  submitValues.saveAsDraft && setLoadingDraft(true);

  try {
    const response = await (await contractService[config.serviceMethod](values)).getResponseData();
    if (response.success) {
      toast.success(config.successMessage);
    } else {
      handleErrors(response);
    }
  } catch (error: any) {
    toast.error(config.errorMessage);
  } finally {
    // Verificar si no hay otros modales de confirmación abiertos
    const hasOtherConfirmationsOpen = Object.values(openConfirmStates).some(state => state);

    if (!hasOtherConfirmationsOpen) {
      if (submitValues.saveAsContract) {
        submitContract(submitValues);
      } else {
        submitDraft(submitValues);
      }
    }
  }
};
